import React from 'react';
import { FileText, Scale, AlertTriangle, CheckCircle, XCircle, DollarSign, Clock } from 'lucide-react';

export const TermsOfService: React.FC = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-primary-600 to-secondary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Scale className="h-16 w-16 text-white mx-auto mb-6" />
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">
              Terms of Service
            </h1>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto leading-relaxed">
              These terms govern your use of our website and services. Please read them carefully.
            </p>
            <p className="text-sm text-primary-200 mt-4">
              Last updated: January 1, 2025
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            
            {/* Introduction */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <FileText className="h-8 w-8 text-primary-600 mr-3" />
                Agreement to Terms
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                These Terms of Service ("Terms") constitute a legally binding agreement between you ("Client") and 
                OutstandingNet ("Company," "we," "our," or "us") regarding your use of our website and services.
              </p>
              <p className="text-gray-600 leading-relaxed">
                By accessing or using our services, you agree to be bound by these Terms. If you do not 
                agree to these Terms, please do not use our services.
              </p>
            </div>

            {/* Services Description */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Services</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                OutstandingNet provides comprehensive web design, development, and digital services including:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
                <li>Custom website design and development</li>
                <li>E-commerce solutions and online stores</li>
                <li>Website maintenance and ongoing support</li>
                <li>Search engine optimization (SEO)</li>
                <li>Digital marketing and advertising services</li>
                <li>Hosting, domain management, and technical support</li>
                <li>Content management and updates</li>
              </ul>
            </div>

            {/* Intellectual Property and Ownership */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Intellectual Property and Digital Asset Ownership</h2>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">OutstandingNet Ownership</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>All website content, designs, code, and digital assets created by OutstandingNet remain the exclusive property of OutstandingNet.</strong> This includes but is not limited to:
                </p>
                <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                  <li>Website designs, layouts, and visual elements</li>
                  <li>Custom code, scripts, and programming</li>
                  <li>Graphics, images, and multimedia content</li>
                  <li>Content management systems and databases</li>
                  <li>SEO configurations and marketing setups</li>
                  <li>All digital assets and intellectual property</li>
                </ul>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Client Purchase Option</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Clients have the right to purchase full ownership of their website content and digital assets from OutstandingNet for the complete agreed-upon project price. Upon full payment of the purchase price:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>All rights, title, and interest transfer to the client</li>
                  <li>Client receives all source files and documentation</li>
                  <li>OutstandingNet provides necessary transfer assistance</li>
                  <li>Client assumes full responsibility for ongoing maintenance</li>
                </ul>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-4">Client-Provided Content</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Content provided by the client (text, images, logos, etc.) remains the property of the client. 
                However, the client grants OutstandingNet a license to use this content for the provision of services.
              </p>
            </div>

            {/* Payment Terms and Billing */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <DollarSign className="h-8 w-8 text-primary-600 mr-3" />
                Payment Terms and Billing
              </h2>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <AlertTriangle className="h-6 w-6 text-red-600 mr-2" />
                  Past Due Invoice Policy
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>If any client invoice becomes past due, OutstandingNet reserves the full right to immediately suspend or shut down website content and services until the client's billing account is brought current.</strong>
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>Website may be taken offline without prior notice</li>
                  <li>All services will be suspended until payment is received</li>
                  <li>Additional fees may apply for service restoration</li>
                  <li>No refunds for downtime due to non-payment</li>
                </ul>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-4">Late Payment Fees</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                <strong>Late payments will incur a 2% fee</strong> on the outstanding balance for each month the payment remains overdue.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-4">Payment Schedule</h3>
              <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
                <li>Project deposits: 50% required before commencement</li>
                <li>Remaining balance: Due upon project completion</li>
                <li>Monthly services: Billed in advance</li>
                <li>Payment terms: Net 15 days unless otherwise specified</li>
              </ul>
            </div>

            {/* Contract Terms and Renewals */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <Clock className="h-8 w-8 text-primary-600 mr-3" />
                Contract Terms and Renewals
              </h2>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Automatic Renewal</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>All contracts are automatically renewed based on the initial contract term</strong> unless proper cancellation notice is provided.
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>Contracts renew for the same duration as the original term</li>
                  <li>Pricing may be adjusted at renewal based on current rates</li>
                  <li>Services continue uninterrupted upon renewal</li>
                  <li>New terms may apply to renewed contracts</li>
                </ul>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-4">Cancellation Policy</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Standard Cancellation</h4>
                  <p className="text-gray-600 text-sm mb-2">
                    <strong>30-day written notice required</strong> before contract renewal date
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• No penalties or fees</li>
                    <li>• Services continue until contract end</li>
                    <li>• Proper transition assistance provided</li>
                  </ul>
                </div>
                
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Early Termination</h4>
                  <p className="text-gray-600 text-sm mb-2">
                    <strong>75% early termination fee</strong> of total remaining payments
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Calculated on remaining contract value</li>
                    <li>• Due immediately upon termination</li>
                    <li>• Services cease upon fee payment</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Service Limitations */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Service Limitations and Maintenance</h2>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Monthly Service Cap</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>Unlimited updates and changes are capped at 5 working hours per month</strong>, including regular maintenance and updates.
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>Includes content updates, minor design changes, and maintenance</li>
                  <li>Hours reset monthly and do not roll over</li>
                  <li>Additional hours available at standard hourly rates</li>
                  <li>Major redesigns or new features require separate agreements</li>
                  <li>Emergency fixes and security updates are prioritized</li>
                </ul>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-4">What's Included in Monthly Maintenance</h3>
              <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
                <li>Content updates and text changes</li>
                <li>Image replacements and basic graphic updates</li>
                <li>Plugin and software updates</li>
                <li>Security monitoring and basic fixes</li>
                <li>Performance optimization</li>
                <li>Backup management</li>
                <li>Technical support and troubleshooting</li>
              </ul>
            </div>

            {/* Client Responsibilities */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <CheckCircle className="h-8 w-8 text-accent-600 mr-3" />
                Client Responsibilities
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                When using our services, clients agree to:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
                <li>Provide accurate and complete information</li>
                <li>Make timely payments according to agreed terms</li>
                <li>Provide necessary content and materials promptly</li>
                <li>Maintain confidentiality of login credentials</li>
                <li>Use services only for lawful purposes</li>
                <li>Comply with all applicable laws and regulations</li>
                <li>Provide proper cancellation notice when required</li>
                <li>Respect intellectual property rights</li>
              </ul>
            </div>

            {/* Prohibited Uses */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <XCircle className="h-8 w-8 text-red-600 mr-3" />
                Prohibited Uses
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Clients may not use our services for:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
                <li>Illegal activities or content</li>
                <li>Harassment, abuse, or harmful behavior</li>
                <li>Spam or unsolicited communications</li>
                <li>Malware, viruses, or malicious code</li>
                <li>Copyright or trademark infringement</li>
                <li>Adult content or gambling (without proper licensing)</li>
                <li>Hate speech or discriminatory content</li>
                <li>Activities that violate third-party rights</li>
              </ul>
            </div>

            {/* Warranties and Disclaimers */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <AlertTriangle className="h-8 w-8 text-yellow-600 mr-3" />
                Warranties and Disclaimers
              </h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Our Warranties</h3>
              <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
                <li>Services performed with professional skill and care</li>
                <li>Work conforms to agreed specifications</li>
                <li>30-day warranty on bug fixes for custom development</li>
                <li>Websites tested across major browsers and devices</li>
                <li>Ongoing support during active service periods</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-4">Disclaimers</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Except as expressly stated, our services are provided "as is" without warranties of any kind. 
                We do not guarantee specific results, search engine rankings, traffic levels, or conversion rates.
              </p>
            </div>

            {/* Limitation of Liability */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Limitation of Liability</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Our total liability for any claims arising from our services shall not exceed the amount 
                paid by the client for the specific service giving rise to the claim. We shall not be liable for:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
                <li>Indirect, incidental, or consequential damages</li>
                <li>Loss of profits, data, or business opportunities</li>
                <li>Third-party actions or content</li>
                <li>Force majeure events beyond our control</li>
                <li>Downtime due to client non-payment</li>
                <li>Data loss due to client actions or negligence</li>
              </ul>
            </div>

            {/* Termination */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Termination</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Upon termination of services (whether by expiration, cancellation, or breach):
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
                <li>All outstanding fees become immediately due</li>
                <li>Early termination fees apply if applicable</li>
                <li>Website content remains property of OutstandingNet unless purchased</li>
                <li>Client data will be provided upon request and payment</li>
                <li>Services cease on the termination date</li>
                <li>Confidential information must be returned</li>
              </ul>
            </div>

            {/* Governing Law */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Governing Law</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                These Terms are governed by the laws of the State of Michigan, United States. Any disputes 
                will be resolved in the courts of Macomb County, Michigan.
              </p>
            </div>

            {/* Changes to Terms */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Changes to Terms</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                We reserve the right to modify these Terms at any time. Changes will be effective upon 
                posting to our website. Continued use of our services constitutes acceptance of modified Terms. 
                Existing contracts will be governed by the terms in effect at the time of signing.
              </p>
            </div>

            {/* Contact Information */}
            <div className="card p-8 bg-gray-50">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Information</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                If you have questions about these Terms of Service, please contact us:
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <span className="text-gray-700">📧 support@outstandingnet.com</span>
                </div>
                <div className="flex items-center">
                  <span className="text-gray-700">📞 (586) 248-4115</span>
                </div>
                <div className="flex items-start">
                  <div className="text-gray-700">
                    📍 11908 Cedarwood Dr<br />
                    &nbsp;&nbsp;&nbsp;&nbsp;Shelby Township, MI 48315
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};